import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScanLine, CheckCircle, XCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import axios from 'axios';
import { toast } from 'sonner';
import Logo from '@/components/Logo';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScannerMobile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [lastScanResult, setLastScanResult] = useState(null);
  const [scanner, setScanner] = useState(null);

  useEffect(() => {
    fetchAuthorizedEvents();
    return () => {
      if (scanner) {
        scanner.stop().catch(err => console.error('Error stopping scanner:', err));
      }
    };
  }, []);

  const fetchAuthorizedEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/scanner/authorized-events`);
      setEvents(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedEvent(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Errore nel caricamento degli eventi');
    }
  };

  const startScanning = async () => {
    try {
      const html5QrCode = new Html5Qrcode('qr-reader');
      setScanner(html5QrCode);
      
      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        onScanSuccess,
        onScanError
      );
      
      setScanning(true);
    } catch (error) {
      console.error('Error starting scanner:', error);
      toast.error('Impossibile avviare la fotocamera');
    }
  };

  const stopScanning = async () => {
    if (scanner) {
      try {
        await scanner.stop();
        setScanning(false);
        setScanner(null);
      } catch (error) {
        console.error('Error stopping scanner:', error);
      }
    }
  };

  const onScanSuccess = async (decodedText) => {
    if (!selectedEvent) {
      toast.error('Seleziona un evento');
      return;
    }

    // Stop scanning temporarily
    await stopScanning();

    try {
      const response = await axios.post(`${API_URL}/checkin`, {
        event_id: selectedEvent.id,
        qr_code: decodedText,
        zone_name: 'Ingresso Principale'
      });

      setLastScanResult({
        success: true,
        message: response.data.message || 'Check-in effettuato!',
        guest_name: response.data.guest_name
      });
      
      toast.success('Check-in effettuato!');
    } catch (error) {
      setLastScanResult({
        success: false,
        message: error.response?.data?.detail || 'Errore nel check-in'
      });
      
      toast.error(error.response?.data?.detail || 'Errore nel check-in');
    }

    // Restart scanning after 2 seconds
    setTimeout(() => {
      startScanning();
      setLastScanResult(null);
    }, 2000);
  };

  const onScanError = (errorMessage) => {
    // Ignore scan errors (normal when no QR in view)
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="mr-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Logo size="md" linkTo="/launcher" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Scanner QR</h1>
              <p className="text-sm text-gray-600">Scansiona biglietti per check-in</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {events.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nessun Evento Autorizzato</h3>
              <p className="text-gray-600">Non sei autorizzato a scansionare biglietti per nessun evento.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Event Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Seleziona Evento</CardTitle>
              </CardHeader>
              <CardContent>
                <select
                  value={selectedEvent?.id || ''}
                  onChange={(e) => {
                    const event = events.find(ev => ev.id === e.target.value);
                    setSelectedEvent(event);
                  }}
                  className="w-full p-2 border rounded-md"
                >
                  {events.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.title}
                    </option>
                  ))}
                </select>
              </CardContent>
            </Card>

            {/* Scanner */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ScanLine className="w-5 h-5" />
                  Scanner QR Code
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div id="qr-reader" className="w-full"></div>
                
                {!scanning ? (
                  <Button
                    onClick={startScanning}
                    className="w-full"
                    size="lg"
                  >
                    <ScanLine className="w-5 h-5 mr-2" />
                    Avvia Scanner
                  </Button>
                ) : (
                  <Button
                    onClick={stopScanning}
                    variant="destructive"
                    className="w-full"
                    size="lg"
                  >
                    Ferma Scanner
                  </Button>
                )}

                {/* Last Scan Result */}
                {lastScanResult && (
                  <div className={`p-4 rounded-lg ${
                    lastScanResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex items-start gap-3">
                      {lastScanResult.success ? (
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                      )}
                      <div>
                        <p className={`font-medium ${
                          lastScanResult.success ? 'text-green-900' : 'text-red-900'
                        }`}>
                          {lastScanResult.message}
                        </p>
                        {lastScanResult.guest_name && (
                          <p className="text-sm text-gray-700 mt-1">
                            Ospite: {lastScanResult.guest_name}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
